import React, { createContext, useEffect, useState, ReactNode } from "react";
import { authService, setAccessToken, SwitcherAccount, UserResponse } from "../services";

interface AuthContextType {
  user: UserResponse | null;
  token: string | null;
  login: (token: string) => void;
  logout: () => void;
  setUserData: (userData: UserResponse) => Promise<void>;
  switcherAccounts: SwitcherAccount[];
  selectedSwitcherAccountId: string | null;
  selectSwitcherAccount: (accountId: string) => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

// Create Context
export const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  login: () => {},
  logout: () => {},
  setUserData: async () => {},
  switcherAccounts: [],
  selectedSwitcherAccountId: null,
  selectSwitcherAccount: async () => {}
});

// Provider Component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserResponse | null>(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('accessToken'));
  const [switcherAccounts, setSwitcherAccounts] = useState<SwitcherAccount[]>([]);
  const [selectedSwitcherAccountId, setSelectedSwitcherAccountId] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem('accessToken');
    if (stored) setAccessToken(stored);
  }, []);

  const login = (accessToken: string) => {
    setToken(accessToken);
    setAccessToken(accessToken);
  };

  const isSuperUser = (roles: any): boolean => {
    const roleList = Array.isArray(roles) ? roles : roles ? [roles] : [];
    return roleList.some((role) => {
      const roleName = typeof role === "string" ? role : role?.name ?? role?.role;
      return roleName?.toLowerCase?.() === "super-user";
    });
  };

  const selectSwitcherAccount = async (accountId: string) => {
    const switcherAccount = await authService.switchAccount(accountId);
    setAccessToken(switcherAccount.accessToken);
    setToken(switcherAccount.accessToken);
    setSelectedSwitcherAccountId(accountId);
  };

  const setUserData = async (userData: UserResponse) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));

    if (isSuperUser(userData.roles)) {
      const accounts = await authService.getSwitcherAccounts();
      setSwitcherAccounts(accounts);
      if (accounts.length) {
        await selectSwitcherAccount(accounts[0].id);
      }
    } else {
      setSwitcherAccounts([]);
      setSelectedSwitcherAccountId(null);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setAccessToken(null);
    setSwitcherAccounts([]);
    setSelectedSwitcherAccountId(null);
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        setUserData,
        switcherAccounts,
        selectedSwitcherAccountId,
        selectSwitcherAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};